/**
 * Returns the abbreviation for the system's current timezone (e.g. "IST", "EST").
 *
 * Browsers expose the short timezone name as a proper abbreviation for some zones
 * (e.g. "EST") but as a GMT offset for others (e.g. India → "GMT+5:30"). When we only
 * get an offset, we derive the abbreviation from the long name's initials
 * ("India Standard Time" → "IST").
 */
export function getSystemTimezoneAbbreviation(date: Date = new Date()): string {
  try {
    const shortName = extractTimeZoneName(date, "short");
    if (shortName && /^[A-Za-z]{2,5}$/.test(shortName)) {
      return shortName.toUpperCase();
    }

    const longName = extractTimeZoneName(date, "long");
    if (longName) {
      const initials = longName
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
      if (initials.length >= 2) return initials;
    }

    return shortName ?? "UTC";
  } catch {
    return "UTC";
  }
}

function extractTimeZoneName(
  date: Date,
  format: "short" | "long"
): string | undefined {
  return new Intl.DateTimeFormat("en-US", { timeZoneName: format })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
}
